import { useCallback, useEffect, useState } from 'react'

import { LoadingSpinner } from '../../../../../components/core-v2'
import {
  actions as builderActions,
  WizardState
} from '../../../../tradebuilder-v2/modules/00-builder/state/builder-slice'
import { useAppDispatch, useAppSelector } from '../../../../tradebuilder-v2/store/hooks'
import { useActions } from '../../../../tradebuilder-v2/store/use-actions'
import { TradeActivationStep } from '../../../../tradebuilder-v2/types'
import {
  actions as commitmentActions,
  postBusinessCommit,
  postClientCommit,
  postTradeReject
} from '../state/trade-commitment-slice'
import TradeCommitmentStateless from '../templates/trade-commitment-stateless'

const TradeCommitment = () => {
  const builderState = useAppSelector(state => state.builder)
  const state = useAppSelector(state => state.tradeCommitment)
  const actions = useActions(commitmentActions)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const dispatch = useAppDispatch()

  const initialise = useCallback(
    (builder: WizardState) => {
      setIsLoading(true)
      actions.reset()
      actions.initialise({
        approvalEvents: builder.approvalEvents,
        accountId: builder.accountId,
        tradeDocumentationUrl:
          builderState.savedState.account.tradeDocumentation[
            builderState.savedState.account.tradeDocumentation.length - 1
          ].relativeUrl,
        status: builder.status,
        userName: builder.userName,
        userType: builder.userType,
        userRoles: builder.userRoles,
        client: builder.client,
        businessUnit: builder.businessUnit,
        cutOffTime: builder.savedState.account.dateActionIsRequiredBy
      })
    },
    [actions, builderState.savedState.account.tradeDocumentation]
  )

  useEffect(() => {
    initialise(builderState)
  }, [builderState, initialise])
  useEffect(() => {
    initialise(builderState)
  }, [builderState.approvalEvents, builderState.accountId, initialise, builderState])

  useEffect(() => {
    setIsLoading(true)
  }, [builderState.selectedAccountId])
  useEffect(() => {
    if (state.accountId === builderState.selectedAccountId) {
      setIsLoading(false)
    }
  }, [builderState.selectedAccountId, state.accountId, state.approvalEvents])
  useEffect(() => {
    if (
      state.accountId === builderState.selectedAccountId &&
      (state.businessApproveSuccess === true || state.clientApproveSuccess === true)
    ) {
      const notificationTitle =
        builderState.status === TradeActivationStep.commitment
          ? 'The trade has been committed to at the Business Unit level'
          : 'The trade has been committed to at the Client level'
      dispatch(
        builderActions.refreshTradeStatus(state.accountId, {
          title: notificationTitle
        })
      )
    }
  }, [
    builderState.selectedAccountId,
    builderState.status,
    state.businessApproveSuccess,
    state.clientApproveSuccess,
    state.accountId,
    dispatch
  ])

  useEffect(() => {
    if (state.accountId === builderState.selectedAccountId && state.rejectSuccess === true) {
      const notificationTitle =
        builderState.status === TradeActivationStep.commitment
          ? 'The trade has been rejected at the Business Unit level'
          : 'The trade has been rejected at the Client level'
      dispatch(
        builderActions.refreshTradeStatus(state.accountId, {
          title: notificationTitle
        })
      )
    }
  }, [
    state.rejectSuccess,
    dispatch,
    state.accountId,
    builderState.selectedAccountId,
    builderState.status
  ])

  const onBusinessCommit = () => {
    dispatch(postBusinessCommit(state.accountId))
    dispatch(actions.setAuthorisedStatus(false))
  }
  const onClientCommit = () => {
    dispatch(postClientCommit(state.accountId))
  }
  const onTradeReject = comments => {
    dispatch(postTradeReject(state.accountId, comments))
  }

  const { pendingAccountState } = builderState.savedState.account
  const displayClientCommit =
    builderState.savedState.account.newAccount ||
    pendingAccountState.deposit !== undefined ||
    pendingAccountState.withdrawal !== undefined ||
    pendingAccountState.closeAccount ||
    pendingAccountState.switchRequest !== undefined

  return (
    <>
      <LoadingSpinner contextName="trade commitment" show={isLoading} />
      {!isLoading && (
        <TradeCommitmentStateless
          displayClientCommit={displayClientCommit}
          state={state}
          onBusinessCommit={onBusinessCommit}
          onClientCommit={onClientCommit}
          onCloseConfirmCommit={() => actions.closeConfirmCommit()}
          onRequestConfirmCommit={() => actions.confirmCommit()}
          onSetAuthorised={isAuthorised => actions.setAuthorisedStatus(isAuthorised)}
          onTradeReject={onTradeReject}
        />
      )}
    </>
  )
}

export default TradeCommitment
